// The operator's Unread answers, the messages of theirs still waiting on a reply, and
// the writes that move them. Mirrors lambda_handlers/api/stateful_agents/runs/
// {unread,opened,set_read}.py. See docs/Y2026-Q4/design-unread-answers.md.
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { QK } from "../queryKeys";
import { useAgentsHttp } from "../useAgentsHttp";

/** What the operator sent: a message, an answer to an agent's question, or a comment. */
export type SentKind = "message" | "answer" | "comment";

/** Why a message has no reply yet. */
export type WaitingState = "working" | "queued" | "stalled";

export interface UnreadConversation {
  run_id: string;
  title: string;
  kind: string;
  /** None on a conversation about no one project. */
  context_project_id: string | null;
  project_name: string;
  sent: { kind: SentKind; text: string; at: string };
  /** The unread replies; null on a row still waiting. */
  reply: {
    /** The first unread reply, where the conversation's New divider goes. */
    message_id: string;
    agent: string | null;
    participants: string[];
    /** The latest unread reply. */
    at: string;
    count: number;
    /** What Mark read on the row marks read through: the replies it shows. */
    through_seq: number;
  } | null;
  state: WaitingState | null;
}

export interface UnreadAnswers {
  /** Newest reply first. */
  unread: UnreadConversation[];
  /** Newest message first. */
  working: UnreadConversation[];
}

export const useUnreadAnswers = (email: string, enabled: boolean) => {
  const http = useAgentsHttp();
  return useQuery<UnreadAnswers>(
    [QK.unreadAnswers, email],
    () => http<UnreadAnswers>("/stateful-runs/unread", { actor_email: email }),
    {
      enabled: enabled && !!email,
      // Polled so a reply landing while the operator is elsewhere shows on the badge.
      refetchInterval: 60_000,
      refetchOnWindowFocus: true,
    },
  );
};

export const useSetRunRead = (email: string) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { runId: string; read: boolean; throughSeq?: number }
  >(
    ({ runId, read, throughSeq }) =>
      http("/stateful-runs/set-read", {
        run_id: runId,
        read,
        through_seq: throughSeq,
        actor_email: email,
      }),
    { onSuccess: () => void qc.invalidateQueries([QK.unreadAnswers]) },
  );
};

/** Mark all as read: every listed conversation, through the reply the page showed. */
export const useSetAllRead = (email: string) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<
    { marked: string[] },
    Error,
    { runId: string; throughSeq: number }[]
  >(
    (rows) =>
      http<{ marked: string[] }>("/stateful-runs/set-read-all", {
        items: rows.map((r) => ({ run_id: r.runId, through_seq: r.throughSeq })),
        actor_email: email,
      }),
    { onSuccess: () => void qc.invalidateQueries([QK.unreadAnswers]) },
  );
};

interface OpenedResponse {
  run_id: string;
  started: boolean;
  first_unread_message_id: string | null;
}

/** Records that the run is in front of the operator, through the last message the page
 * shows, and again whenever a newer one arrives while it is. ``seen`` is false while
 * the transcript is mounted but hidden. Returns the reply the New divider sits above:
 * the first one they had not read before this open. On a scheduled run the server
 * also starts its Gmail + Drive refresh on the first open. */
export const useRunOpened = ({
  runId,
  throughSeq,
  seen,
  email,
}: {
  runId: string;
  throughSeq: number | null;
  seen: boolean;
  email: string;
}) => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  const recorded = useRef<{ runId: string; seq: number } | null>(null);
  const [firstUnread, setFirstUnread] = useState<string | null>(null);
  const { mutate, error } = useMutation<OpenedResponse, Error, number>(
    (seq) =>
      http<OpenedResponse>("/stateful-runs/opened", {
        run_id: runId,
        through_seq: seq,
        actor_email: email,
      }),
    {
      onSuccess: (data) => {
        if (data.first_unread_message_id)
          setFirstUnread(data.first_unread_message_id);
        void qc.invalidateQueries([QK.unreadAnswers]);
      },
    },
  );
  useEffect(() => {
    if (!seen || !runId || throughSeq === null) return;
    const last = recorded.current;
    // Once per run per new message (StrictMode double-fires effects in dev).
    if (last && last.runId === runId && last.seq >= throughSeq) return;
    if (last && last.runId !== runId) setFirstUnread(null);
    recorded.current = { runId, seq: throughSeq };
    mutate(throughSeq);
  }, [seen, runId, throughSeq, mutate]);
  return { firstUnread, error };
};
